import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const ReviewsMarquee = () => {
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState({ rating_value: 0, review_count: 0 });
  const [buttonConfig, setButtonConfig] = useState({
    text: "Lascia una recensione",
    url: "https://g.page/r/CR7pYlDG6yGfEAI/review",
    background_color: "#4285F4",
    text_color: "#FFFFFF",
    border_radius: "full",
    is_visible: true
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load reviews
        const reviewsRes = await api.get("/reviews");
        setReviews(reviewsRes.data.reviews || []);
        setAggregate(reviewsRes.data.aggregate || { rating_value: 0, review_count: 0 });
        
        // Load button config
        const contentRes = await api.get("/content");
        if (contentRes.data?.reviews_button) {
          setButtonConfig(prev => ({ ...prev, ...contentRes.data.reviews_button }));
        }
      } catch (err) {
        console.log("Error loading data");
      }
    };
    loadData();
  }, []);

  if (reviews.length === 0) return null;

  // Duplicate reviews for seamless loop
  const duplicatedReviews = [...reviews, ...reviews];

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );

  return (
    <section className="py-12 md:py-16 bg-[#2C2C2C] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 
              className="text-2xl md:text-3xl font-bold text-white mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Cosa dicono di noi
            </h2>
            <p className="text-white/70">Le recensioni dei nostri ospiti</p>
          </div>
          
          <div className="flex items-center gap-4 bg-white/10 rounded-2xl px-6 py-4">
            <div className="text-center">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-white">{aggregate.rating_value}</span>
                <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              </div>
              <p className="text-white/70 text-sm">{aggregate.review_count} recensioni</p>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee Container */}
      <div className="relative">
        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-20 md:w-40 bg-gradient-to-r from-[#2C2C2C] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 md:w-40 bg-gradient-to-l from-[#2C2C2C] to-transparent z-10 pointer-events-none" />
        
        {/* Scrolling Reviews */}
        <div className="flex animate-marquee hover:pause-animation">
          {duplicatedReviews.map((review, index) => (
            <div
              key={`${review.id}-${index}`}
              className="flex-shrink-0 w-[320px] md:w-[400px] mx-3 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-semibold text-white">{review.author_name}</p>
                  <p className="text-white/50 text-sm">
                    {new Date(review.date_published).toLocaleDateString('it-IT', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                {renderStars(review.rating)}
              </div>
              
              <p className="text-white/80 text-sm leading-relaxed line-clamp-4">
                "{review.review_text}"
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Google/Tripadvisor Badge */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
        <div className="flex items-center justify-center gap-2 text-white/50 text-sm">
          <span>Recensioni verificate da</span>
          <span className="font-semibold text-white/70">Google</span>
          <span>&</span>
          <span className="font-semibold text-white/70">Tripadvisor</span>
        </div>
      </div>

      {/* Review Button */}
      {buttonConfig.is_visible && buttonConfig.url && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8 flex justify-center">
          <a
            href={buttonConfig.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-8 py-4 font-semibold text-lg transition-all hover:scale-105 hover:shadow-lg ${
              buttonConfig.border_radius === 'full' ? 'rounded-full' :
              buttonConfig.border_radius === 'lg' ? 'rounded-lg' :
              buttonConfig.border_radius === 'md' ? 'rounded-md' :
              buttonConfig.border_radius === 'none' ? 'rounded-none' : 'rounded-full'
            }`}
            style={{
              backgroundColor: buttonConfig.background_color,
              color: buttonConfig.text_color
            }}
          >
            {buttonConfig.text}
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      )}

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        
        .pause-animation {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default ReviewsMarquee;
